import { useEffect, useState } from "react";
import { Inbox, Hourglass, MapPin, Star, BadgeCheck, Archive, ArrowLeft, Check } from "lucide-react";
import { api } from "../lib/api";
import { ARCHIVE_REASONS } from "../lib/constants";

const SECTIONS = [
  { key: "all", label: "All leads", icon: Inbox },
  { key: "future", label: "Future Leads", icon: Hourglass },
  { key: "other", label: "Around Me", icon: MapPin, pickRep: true },
  { key: "potential", label: "Potential", icon: Star, pickRep: true },
  { key: "signed", label: "Signed", icon: BadgeCheck, pickRep: true, noMine: true },
  { key: "archived", label: "Dead", icon: Archive },
];

// Reps: book or kill open leads, send their own Around Me back, restore their own Dead deals.
const repChoices = (cur) => ({ all: ["potential", "archived"], future: ["potential", "archived"], other: ["all", "potential", "archived"], archived: ["all"] })[cur] || [];

export const whereChoices = (lead, isAdmin) => {
  const cur = lead?.bucket || "all";
  return isAdmin ? SECTIONS.map((s) => s.key) : [cur, ...repChoices(cur)];
};

const Btn = ({ s, here, onClick, label, testid }) => (
  <button onClick={onClick} disabled={here} data-testid={testid}
    className={`h-12 rounded-2xl border px-3 text-sm font-semibold flex items-center gap-2 text-left transition-colors ${here ? "bg-blue-700 border-blue-700 text-white cursor-default" : "bg-white border-slate-200 text-slate-800 hover:border-blue-500 hover:bg-blue-50"}`}>
    {here ? <Check className="h-4 w-4 shrink-0" /> : <s.icon className="h-4 w-4 shrink-0 text-blue-700" />}
    <span className="leading-tight">{label}{here && <span className="block text-[10px] font-medium opacity-80">You're here</span>}</span>
  </button>
);

// The one place to move a lead (or a BIG group) between sections. Jeff picks the rep and the Dead reason inline.
export const WhereToNext = ({ lead, current, isAdmin, myId, allLabel = "All leads", onGo, testid = "where-to-next", leadState }) => {
  const [step, setStep] = useState(null);
  const [reps, setReps] = useState(null);
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState("");
  const [repFilter, setRepFilter] = useState("all");
  const [repSearch, setRepSearch] = useState("");
  useEffect(() => { setStep(null); setNote(""); setMsg(""); }, [lead?.id]);
  useEffect(() => {
    if (step?.pickRep && isAdmin && !reps) api.get("/users/names").then((r) => setReps(r.data.filter((u) => u.id !== myId))).catch((e) => console.error("load reps", e));
  }, [step, isAdmin, reps, myId]);
  const cur = current === undefined ? lead?.bucket || "all" : current;
  const keys = whereChoices(lead, isAdmin);
  if (keys.length < 2 && current === undefined) return null;
  const label = (s) => (s.key === "all" ? allLabel : s.label);
  const send = (m) => onGo(isAdmin && msg.trim() ? { ...m, jeff_note: msg.trim() } : m);
  const go = (s) => {
    if (s.key === "archived") return setStep(s);
    if (s.pickRep && isAdmin) return setStep(s);
    send({ bucket: s.key === "all" ? null : s.key });
  };
  const shown = SECTIONS.filter((s) => keys.includes(s.key));
  const st = (leadState || lead?.state || "").toUpperCase();
  const shownReps = (reps || []).filter((u) => (repFilter === "none" ? !u.allowed_states.length : repFilter === "covers" ? (!u.allowed_states.length && !u.no_states) || u.allowed_states.includes(st) : true)
    && u.name.toLowerCase().includes(repSearch.trim().toLowerCase()));
  const back = <button onClick={() => setStep(null)} className="text-xs font-semibold text-blue-700 flex items-center gap-1" data-testid={`${testid}-back`}><ArrowLeft className="h-3.5 w-3.5" />Back</button>;
  return (
    <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <p className="font-display text-base font-extrabold text-slate-900">Where to Next?</p>
        {step && back}
      </div>
      {!step && (
        <div className="grid grid-cols-2 gap-2">
          {shown.map((s, i) => (
            <div key={s.key} className={shown.length % 2 && i === shown.length - 1 ? "col-span-2 grid" : "grid"}>
              <Btn s={s} here={s.key === cur} label={label(s)} onClick={() => go(s)} testid={`${testid}-${s.key}`} />
            </div>
          ))}
        </div>
      )}
      {step?.pickRep && (
        <div className="space-y-2" data-testid={`${testid}-reps`}>
          <p className="text-xs text-slate-500">Whose {step.label}?</p>
          <div className="flex flex-wrap items-center gap-1.5">
            {[["all", "All reps"], ["none", "No states"], ...(st ? [["covers", `Covers ${st}`]] : [])].map(([k, l]) => (
              <button key={k} onClick={() => setRepFilter(k)} data-testid={`${testid}-rep-filter-${k}`}
                className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${repFilter === k ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"}`}>{l}</button>
            ))}
            <input value={repSearch} onChange={(e) => setRepSearch(e.target.value)} placeholder="Search reps" data-testid={`${testid}-rep-search`}
              className="h-8 flex-1 min-w-[8rem] rounded-full border border-slate-200 bg-white px-3 text-xs" />
          </div>
          {!step.noMine && <button onClick={() => send({ bucket: step.key, for_uid: null })} className="w-full h-10 rounded-full bg-yellow-300 hover:bg-yellow-400 text-yellow-950 text-sm font-semibold transition-colors" data-testid={`${testid}-mine`}>My {step.label}</button>}
          {shownReps.map((u) => {
            const out = st && u.allowed_states.length > 0 && !u.allowed_states.includes(st);
            return (
              <button key={u.id} disabled={out} onClick={() => send({ bucket: step.key, for_uid: u.id, forName: u.name })} data-testid={`${testid}-rep-${u.id}`}
                title={out ? `${u.name} can't see ${st} leads` : undefined}
                className="w-full min-h-10 py-1.5 rounded-full border border-slate-200 bg-white text-sm font-semibold text-slate-800 hover:border-blue-500 disabled:opacity-45 disabled:hover:border-slate-200 disabled:cursor-not-allowed transition-colors">
                {u.name}
                <span className="block text-[10px] font-medium text-slate-500">{u.allowed_states.length ? u.allowed_states.join(" · ") : u.no_states ? "No states (sees only what you send)" : "All states"}{out ? ` · not ${st}` : ""}</span>
              </button>
            );
          })}
          {reps && !shownReps.length && <p className="text-xs text-slate-400" data-testid={`${testid}-no-reps`}>No reps match.</p>}
          {!reps && <p className="text-xs text-slate-400">Loading reps…</p>}
        </div>
      )}
      {step?.key === "archived" && (
        <div className="space-y-2" data-testid={`${testid}-reasons`}>
          <p className="text-xs text-slate-500">Why is it dead?</p>
          <div className="grid grid-cols-2 gap-2">
            {ARCHIVE_REASONS.filter((r) => r.value !== "other").map((r) => (
              <button key={r.value} onClick={() => send({ bucket: "archived", reason: r.value })} data-testid={`${testid}-reason-${r.value}`}
                className="h-10 rounded-full border border-slate-200 bg-white text-sm font-semibold text-slate-800 hover:border-slate-900 transition-colors">{r.label}</button>
            ))}
          </div>
          <div className="flex gap-2">
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="Other reason…" data-testid={`${testid}-other-note`}
              className="flex-1 h-10 rounded-full border border-slate-200 px-4 text-sm bg-white" />
            <button disabled={!note.trim()} onClick={() => send({ bucket: "archived", reason: "other", note: note.trim() })} data-testid={`${testid}-reason-other`}
              className="h-10 px-4 rounded-full bg-slate-900 text-white text-sm font-semibold disabled:opacity-40 transition-colors">Move to Dead</button>
          </div>
        </div>
      )}
      {isAdmin && (
        <textarea value={msg} onChange={(e) => setMsg(e.target.value)} maxLength={500} rows={2} placeholder="Note (optional): kept on the lead for the rep and Lucas"
          className="mt-3 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm placeholder:text-slate-400" data-testid={`${testid}-jeff-note`} />
      )}
    </div>
  );
};
